import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ReorderIcon } from '@treams/treams-icons';
import { Button, Heading, Highlight, PlainInput, Spinner, WorkingArea } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalizedState from 'packages/kakapo-ui-application/hooks/useLocalizedState';

const Title = styled(Flex)<{ dragging?: boolean }>`
	border-radius: 5px;

	${(props) =>
		!props.dragging &&
		`
        cursor: grab;

        &:hover {
            background: ${props.theme.colors.lightBlue};

            &:active {
                background: ${props.theme.colors.lightBlueDark};
            }
        }
    `}
`;

const Handle = styled(Box)`
	width: 24px;
	height: 24px;

	svg path {
		fill: ${(props) => props.theme.colors.slateBlue};
	}
`;

export function LibraryCategoryTitle(props: {
	active?: boolean;
	query?: string;
	name: graphql.LocalizedString;
	isArchived: boolean;
	loading?: boolean;
	dragging?: boolean;
	listeners?: object;
	onUpdate?: (name: graphql.LocalizedString) => Promise<void>;
	onArchive?: () => Promise<void>;
	onRestore?: () => Promise<void>;
}) {
	const { t } = useTranslation();

	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(props.name);
	const [localizedName, setLocalizedName] = useLocalizedState(name, setName);

	const loading = props.loading || (props.dragging && !props.active);

	return (
		<Flex mb={3} px={2} alignItems="center">
			{props.isArchived || !props.onUpdate ? (
				<Heading px={2}>
					<Highlight query={props.query}>{localizedName ?? ''}</Highlight>
				</Heading>
			) : (
				<Title
					px={2}
					py={2}
					my={-2}
					dragging={props.dragging}
					{...(loading ? {} : props.listeners)}
				>
					<Flex height={24} width={24} mr={2} justifyContent="center" alignItems="center">
						{loading ? (
							<Spinner />
						) : (
							<Handle>
								<ReorderIcon />
							</Handle>
						)}
					</Flex>
					<WorkingArea
						flexible
						active={editing}
						onCancel={() => {
							setName(props.name);
							setEditing(false);
						}}
						onSave={async () => {
							await props.onUpdate?.(name);

							setEditing(false);
						}}
					>
						{(active) =>
							active ? (
								<Heading style={{ background: 'white', borderRadius: 5 }} p={2} ml={-2} mt={-2}>
									<PlainInput
										value={localizedName}
										onChange={setLocalizedName}
										autoFocus
										multiline={false}
										onFocus={() => {
											setTimeout(() => {
												document.execCommand('selectAll', false, undefined);
											});
										}}
									/>
								</Heading>
							) : (
								<Heading>
									<Highlight query={props.query}>{localizedName ?? ''}</Highlight>
								</Heading>
							)
						}
					</WorkingArea>
				</Title>
			)}
			{!props.isArchived && props.onUpdate && (
				<Button
					type="transparent"
					small
					title={t('settings.feedbackLibrary.category.edit.button')}
					ml={2}
					onClick={() => setEditing(true)}
				/>
			)}
			<Box flexGrow={1} />
			{props.isArchived
				? props.onRestore && (
						<Button
							type="destructive"
							small
							title={t('settings.feedbackLibrary.category.restore.button')}
							ml={2}
							onClick={async () => {
								await props.onRestore?.();
							}}
						/>
				  )
				: props.onArchive && (
						<Button
							type="destructive"
							small
							title={t('settings.feedbackLibrary.category.archive.button')}
							ml={2}
							onClick={async () => {
								await props.onArchive?.();
							}}
						/>
				  )}
		</Flex>
	);
}
